import React, { useState, useEffect } from "react";
import {
  Search, Eye, Edit, X, AlertCircle, Plus, Trash2,
  Webhook, Globe, ArrowRight
} from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import {
  listGlobalApiPresets, showGlobalApiPreset,
  createGlobalApiPreset, updateGlobalApiPreset, deleteGlobalApiPreset
} from "../../services/adminApiPresets";

const ApiPresets = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ pageNumber: 1, pageSize: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({ search: "", provider: "", isActive: "" });
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: "create", item: null });
  const [detailModal, setDetailModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null, loading: false });

  useEffect(() => { load(1); }, []);

  const load = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const query = { pageNumber: page, pageSize: 20, sortBy: "createdAt", sortOrder: "DESC" };
      if (filters.search) query.search = filters.search;
      if (filters.provider) query.provider = filters.provider;
      if (filters.isActive !== "") query.isActive = filters.isActive;
      const data = await listGlobalApiPresets(query);
      setItems(data.items || []);
      setPagination(data.pagination || {});
    } catch (err) { setError(err.message || "Erro ao carregar presets"); }
    finally { setLoading(false); }
  };

  const handleSearchSubmit = (e) => { e.preventDefault(); load(1); };
  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => { setFilters({ search: "", provider: "", isActive: "" }); setTimeout(() => load(1), 0); };

  const openCreate = () => setModal({ open: true, mode: "create", item: { name: "", description: "", provider: "webhook", configuration: { url: "", method: "POST", headers: {}, requestBody: "" }, isActive: true } });
  const openEdit = async (row) => {
    try {
      const data = await showGlobalApiPreset(row.id);
      setModal({ open: true, mode: "edit", item: data });
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao buscar preset" }); }
  };
  const openDetail = async (row) => {
    try {
      const data = await showGlobalApiPreset(row.id);
      setDetailModal({ open: true, item: data });
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao buscar preset" }); }
  };

  const handleSave = async () => {
    const { mode, item } = modal;
    if (!item.name?.trim()) { setToast({ type: "error", message: "Nome é obrigatório" }); return; }
    if (!item.provider?.trim()) { setToast({ type: "error", message: "Provider é obrigatório" }); return; }
    try {
      const payload = { name: item.name, description: item.description, provider: item.provider, configuration: item.configuration, isActive: item.isActive };
      if (mode === "create") {
        await createGlobalApiPreset(payload);
        setToast({ type: "success", message: "Preset criado" });
      } else {
        await updateGlobalApiPreset(item.id, payload);
        setToast({ type: "success", message: "Preset atualizado" });
      }
      setModal({ open: false, mode: "create", item: null });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao salvar" }); }
  };

  const handleDelete = async () => {
    if (!deleteModal.item) return;
    setDeleteModal(prev => ({ ...prev, loading: true }));
    try {
      await deleteGlobalApiPreset(deleteModal.item.id);
      setToast({ type: "success", message: "Preset excluído" });
      setDeleteModal({ open: false, item: null, loading: false });
      load(pagination.pageNumber);
    } catch (err) { setToast({ type: "error", message: err.message || "Erro ao excluir" }); setDeleteModal(prev => ({ ...prev, loading: false })); }
  };

  const columns = [
    { header: "ID", render: (row) => <span className="font-mono text-purple-600 dark:text-purple-400">#{row.id}</span> },
    { header: "Nome", render: (row) => <span className="font-bold text-gray-900 dark:text-gray-100">{row.name}</span> },
    { header: "Provider", render: (row) => <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 font-semibold">{row.provider}</span> },
    { header: "Método", render: (row) => <span className="font-mono text-xs font-bold">{row.method}</span> },
    { header: "URL", render: (row) => <span className="text-xs text-gray-500 truncate max-w-[200px] block" title={row.url}>{row.url || "—"}</span> },
    { header: "Status", render: (row) => <StatusBadge status={row.isActive ? "active" : "inactive"} label={row.isActive ? "Ativo" : "Inativo"} /> },
    { header: "Atualizado", render: (row) => <span className="text-xs text-gray-500">{new Date(row.updatedAt).toLocaleDateString("pt-BR")}</span> },
    {
      header: "Ações", render: (row) => (
        <div className="flex items-center justify-end gap-1.5">
          <button onClick={() => openDetail(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors" title="Visualizar"><Eye size={15} /></button>
          <button onClick={() => openEdit(row)} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-purple-500/20 text-purple-600 dark:text-purple-400 transition-colors" title="Editar"><Edit size={15} /></button>
          <button onClick={() => setDeleteModal({ open: true, item: row, loading: false })} className="p-1 rounded-lg bg-black/5 dark:bg-white/5 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 transition-colors" title="Excluir"><Trash2 size={15} /></button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <PageHeader breadcrumb="Plataforma" title="Presets de Integração" subtitle={`${pagination.total || 0} preset(s) de integração`} action={
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow"><Plus size={14} /> Novo Preset</button>
      } />

      {error && <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3"><AlertCircle size={16} /> {error}</div>}
      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"}`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      <form onSubmit={handleSearchSubmit} className="flex gap-2.5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-3 text-gray-400 dark:text-gray-500" />
          <input type="text" value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)} placeholder="Buscar por nome, provider..." className="w-full pl-9 pr-4 py-2 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-purple-500" />
        </div>
        <button type="submit" className="px-4 py-2 create-event-btn text-white text-xs font-bold rounded-xl shadow-purple-glow">Filtrar</button>
        <button type="button" onClick={clearFilters} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-bold text-gray-700 dark:text-gray-300 rounded-xl">Limpar</button>
      </form>

      <div className="flex flex-wrap gap-2">
        <select value={filters.isActive} onChange={(e) => handleFilterChange("isActive", e.target.value)} className="px-3 py-1.5 bg-white dark:bg-[#232628] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:border-purple-500">
          <option value="">Todos os status</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
      </div>

      <DataTable columns={columns} data={items} loading={loading} emptyTitle="Nenhum preset encontrado" emptyDescription="Crie o primeiro preset global da plataforma." />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
          <span>Página {pagination.pageNumber} de {pagination.totalPages} ({pagination.total} presets)</span>
          <div className="flex gap-2">
            <button disabled={pagination.pageNumber <= 1} onClick={() => load(pagination.pageNumber - 1)} className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10">Anterior</button>
            <button disabled={pagination.pageNumber >= pagination.totalPages} onClick={() => load(pagination.pageNumber + 1)} className="px-3 py-1 rounded-lg bg-gray-100 dark:bg-white/5 disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-white/10">Próxima</button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-2xl p-6 border border-gray-200 dark:border-white/10 relative space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{modal.mode === "create" ? "Novo Preset Global" : "Editar Preset Global"}</h3>
              <button onClick={() => setModal({ open: false, mode: "create", item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Nome *</label>
                  <input type="text" value={modal.item?.name || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, name: e.target.value } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Provider *</label>
                  <input type="text" value={modal.item?.provider || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, provider: e.target.value } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Descrição</label>
                <textarea value={modal.item?.description || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, description: e.target.value } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Método</label>
                  <select value={modal.item?.configuration?.method || "POST"} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, configuration: { ...prev.item.configuration, method: e.target.value } } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500">
                    <option value="GET">GET</option><option value="POST">POST</option><option value="PUT">PUT</option><option value="PATCH">PATCH</option><option value="DELETE">DELETE</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Status</label>
                  <select value={modal.item?.isActive ? "true" : "false"} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, isActive: e.target.value === "true" } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500">
                    <option value="true">Ativo</option><option value="false">Inativo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">URL de Destino</label>
                <input type="text" value={modal.item?.configuration?.url || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, configuration: { ...prev.item.configuration, url: e.target.value } } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500" placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Headers (JSON)</label>
                <textarea value={JSON.stringify(modal.item?.configuration?.headers || {}, null, 2)} onChange={(e) => { try { setModal(prev => ({ ...prev, item: { ...prev.item, configuration: { ...prev.item.configuration, headers: JSON.parse(e.target.value) } } })); } catch {} }} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white font-mono focus:outline-none focus:border-purple-500" rows={3} />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1 block">Body (JSON)</label>
                <textarea value={modal.item?.configuration?.requestBody || ""} onChange={(e) => setModal(prev => ({ ...prev, item: { ...prev.item, configuration: { ...prev.item.configuration, requestBody: e.target.value } } }))} className="w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white font-mono focus:outline-none focus:border-purple-500" rows={4} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
              <button onClick={() => setModal({ open: false, mode: "create", item: null })} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">Cancelar</button>
              <button onClick={handleSave} className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl">{modal.mode === "create" ? "Criar" : "Salvar"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModal.open && detailModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-lg p-6 border border-gray-200 dark:border-white/10 relative space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{detailModal.item.name}</h3>
              <button onClick={() => setDetailModal({ open: false, item: null })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-gray-500">ID:</span> <span className="font-mono">#{detailModal.item.id}</span></div>
                <div><span className="text-gray-500">Provider:</span> <span className="font-semibold">{detailModal.item.provider}</span></div>
                <div><span className="text-gray-500">Método:</span> <span className="font-mono font-bold">{detailModal.item.configuration?.method}</span></div>
                <div><span className="text-gray-500">Status:</span> <StatusBadge status={detailModal.item.isActive ? "active" : "inactive"} label={detailModal.item.isActive ? "Ativo" : "Inativo"} /></div>
              </div>
              {detailModal.item.description && <div><span className="text-gray-500">Descrição:</span> <span>{detailModal.item.description}</span></div>}
              <div><span className="text-gray-500">URL:</span> <span className="font-mono">{detailModal.item.configuration?.url || "—"}</span></div>
              {detailModal.item.configuration?.headers && Object.keys(detailModal.item.configuration.headers).length > 0 && (
                <div><span className="text-gray-500">Headers:</span><pre className="mt-1 p-2 bg-gray-50 dark:bg-[#191c1e] rounded-lg text-[10px] font-mono overflow-x-auto">{JSON.stringify(detailModal.item.configuration.headers, null, 2)}</pre></div>
              )}
              {detailModal.item.configuration?.requestBody && (
                <div><span className="text-gray-500">Body:</span><pre className="mt-1 p-2 bg-gray-50 dark:bg-[#191c1e] rounded-lg text-[10px] font-mono overflow-x-auto">{detailModal.item.configuration.requestBody}</pre></div>
              )}
              <div><span className="text-gray-500">Criado:</span> {new Date(detailModal.item.createdAt).toLocaleString("pt-BR")}</div>
              <div><span className="text-gray-500">Atualizado:</span> {new Date(detailModal.item.updatedAt).toLocaleString("pt-BR")}</div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="neumorphic-card rounded-3xl w-full max-w-md p-6 border border-gray-200 dark:border-white/10 relative space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Confirmar Exclusão</h3>
              <button onClick={() => setDeleteModal({ open: false, item: null, loading: false })} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300">Deseja excluir o preset <strong>{deleteModal.item?.name}</strong>?</p>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
              <button onClick={() => setDeleteModal({ open: false, item: null, loading: false })} className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">Cancelar</button>
              <button onClick={handleDelete} disabled={deleteModal.loading} className="px-5 py-2 bg-rose-500 hover:bg-rose-600 text-xs font-bold text-white rounded-xl">{deleteModal.loading ? "Excluindo..." : "Excluir"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiPresets;
